/** Central app store using Preact signals.
 *
 * Components subscribe via `useSignalEffect` / reading `.value`. Mutations
 * flow through the action functions below so we keep a single source of
 * truth for jobs, config, filters, and scan status.
 */

import { signal, computed } from "@preact/signals";

import type { Config, Filters, Job, ScanError, ScanRun, ScanStatus } from "../lib/types";

// --- Filters (defaults match the legacy UI) ---

export const defaultFilters: Filters = {
  q: "",
  platform: "all",
  timeframe: "all",
  state: "",
  company: "",
  profile: "",
  tag: "",
  sort: "newest",
  showDuplicates: false,
  runId: null,
};

export const filters = signal<Filters>({ ...defaultFilters });
export const currentPage = signal<number>(1);
export const jobsPerPage = signal<number>(24);

// --- Data ---

export const allJobs = signal<Job[]>([]);
export const config = signal<Config | null>(null);
export const scanStatus = signal<ScanStatus | null>(null);
export const scanHistory = signal<ScanRun[]>([]);
export const scanErrors = signal<ScanError[]>([]);

// --- UI state ---

export const activeJobId = signal<string | null>(null);
export const isScanning = signal<boolean>(false);
export const toastMessage = signal<string | null>(null);

// --- Derived ---

export const filteredJobs = computed(() => {
  // Lazy import to avoid circular init issues — filtering.ts has no
  // side effects so a dynamic require-style import isn't needed; we
  // import statically below.
  return filteredJobsImpl(allJobs.value, filters.value);
});

// Imported after to avoid circular reference at module load.
import { getFilteredJobs } from "../lib/filtering";
function filteredJobsImpl(jobs: Job[], f: Filters): Job[] {
  return getFilteredJobs(jobs, f);
}

export const watchlist = computed<string[]>(() => config.value?.watchlist ?? []);

// --- Actions ---

export function setFilter<K extends keyof Filters>(key: K, value: Filters[K]): void {
  filters.value = { ...filters.value, [key]: value };
  if (key !== "q" && key !== "sort") currentPage.value = 1;
}

export function resetFilters(): void {
  filters.value = { ...defaultFilters };
  currentPage.value = 1;
}

let toastTimer: number | null = null;
export function showToast(message: string, durationMs = 3500): void {
  toastMessage.value = message;
  if (toastTimer !== null) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    toastMessage.value = null;
    toastTimer = null;
  }, durationMs);
}
